
import React, { useState, useRef } from 'react';
import { parseExcelFile, CycleImportRow } from '@/utils/cycle-tracking/import';
import { ValidationError } from '@/utils/cycle-tracking/types';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';

// Import our refactored components
import FileUploadSection from './form-sections/FileUploadSection';
import ValidationSection from './form-sections/ValidationSection';
import DataPreviewSection from './form-sections/DataPreviewSection';

interface CycleImportFormProps {
  onImportComplete: (data: CycleImportRow[]) => Promise<void>;
  isUploading?: boolean;
}

const CycleImportForm: React.FC<CycleImportFormProps> = ({ onImportComplete, isUploading = false }) => {
  const [file, setFile] = useState<File | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [parsedData, setParsedData] = useState<CycleImportRow[]>([]);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [warnings, setWarnings] = useState<ValidationError[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    
    setFile(selectedFile);
    setIsValidating(true);
    
    try {
      const { data, errors, warnings } = await parseExcelFile(selectedFile);
      
      setParsedData(data);
      setErrors(errors);
      setWarnings(warnings);
      
      if (errors.length > 0) {
        toast.error(`File contains ${errors.length} error(s) that must be fixed`);
      } else if (warnings.length > 0) {
        toast.warning(`File contains ${warnings.length} warning(s) that you may want to review`);
      } else if (data.length === 0) {
        toast.error('No valid data found in the file');
      } else {
        toast.success(`Successfully parsed ${data.length} cycle entries`);
      }
    } catch (error) {
      console.error('Error parsing file:', error);
      toast.error('Failed to parse file. Please check the format and try again.');
    } finally {
      setIsValidating(false);
    }
  };
  
  const handleReset = () => {
    setFile(null);
    setParsedData([]);
    setErrors([]);
    setWarnings([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const handleImport = async () => {
    if (parsedData.length === 0) {
      toast.error('No valid data to import');
      return;
    }
    
    if (errors.length > 0) {
      toast.error('Please fix all errors before importing');
      return;
    }
    
    try {
      await onImportComplete(parsedData);
    } catch (error) {
      console.error('Error during import:', error);
      toast.error('Failed to import data. Please try again.');
    }
  };
  
  return (
    <div className="space-y-6">
      <FileUploadSection 
        file={file}
        fileInputRef={fileInputRef}
        isValidating={isValidating}
        isUploading={isUploading}
        onFileChange={handleFileChange}
        onReset={handleReset}
        onImport={handleImport}
        hasParsedData={parsedData.length > 0}
        hasErrors={errors.length > 0}
        errors={errors}
        warnings={warnings}
      />
      
      <DataPreviewSection parsedData={parsedData} />
    </div>
  );
};

export default CycleImportForm;
